/**
 * Scenario comparison — run the planning engine across every scenario in a
 * workspace and return the outputs side by side.
 *
 * This is the "scenario planning" capability proper: same priorities, work
 * units, and dependencies; per-scenario assumptions (mode, horizon, buffer)
 * and per-scenario capacity models. Pure read + in-memory calculation — no
 * writes, consistent with the engine being a pre-POR workbench (build
 * contract §4).
 *
 * Every scenario is run against the SAME active Input Scope — the whole
 * point of a comparison is an apples-to-apples read across scenarios, so
 * "compare against pool X" must mean all of them, not each scenario
 * wandering off to its own (or no) scope. Previously this called runEngine,
 * an older, unscoped path that fetches every planning_priorities row in the
 * project directly and accepts no scope argument at all — Input Scope was
 * silently ignored for every scenario, every time.
 */

import { runEngineScoped, type EngineInputSource } from './planningEngineService';
import type { PlanningEngineOutput, Scenario } from './planningEngineModule.types';
import type { Item } from '../types/prioritization/lifecycle';
import type { ItemRelationshipEdge } from '../types/prioritization/itemRelationships';

export interface ScenarioRunResult {
  scenario: Scenario;
  output: PlanningEngineOutput | null;
  error: string | null;
}

export interface ScenarioComparison {
  results: ScenarioRunResult[];
  /** Union of priority IDs seen across all successful runs, in first-seen rank order. */
  priorityIds: string[];
  priorityNames: Map<string, string>;
  generatedAt: string;
}

/**
 * Run the engine once per scenario, all against the same scoped item set.
 * Runs are sequential to keep the DB reads gentle; each run re-reads the
 * scenario's own capacity rows.
 */
export async function runScenarioComparison(
  projectId: string,
  scenarios: Scenario[],
  items: Item[],
  edges: ItemRelationshipEdge[],
  scopedItemIds: Set<string> | null,
  scopeKind: EngineInputSource['type'],
): Promise<ScenarioComparison> {
  const results: ScenarioRunResult[] = [];
  for (const scenario of scenarios) {
    try {
      const output = await runEngineScoped({
        projectId,
        scenario,
        items,
        edges,
        scopedItemIds,
        scopeKind,
      });
      results.push({ scenario, output, error: null });
    } catch (err) {
      results.push({
        scenario,
        output: null,
        error: err instanceof Error ? err.message : 'Engine run failed',
      });
    }
  }

  const priorityIds: string[] = [];
  const priorityNames = new Map<string, string>();
  for (const r of results) {
    for (const ps of r.output?.prioritySummaries ?? []) {
      if (!priorityNames.has(ps.priority.id)) {
        priorityIds.push(ps.priority.id);
        priorityNames.set(ps.priority.id, ps.priority.name);
      }
    }
  }

  return { results, priorityIds, priorityNames, generatedAt: new Date().toISOString() };
}
